/**
 * Insurance explain route guard
 *
 * Redirects away from an explain screen when the patient's billing type or blocked
 * variant does not match the screen the component expects.
 *
 * References:
 * - `insurance/routing.ts`
 */

import { useEffect, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  resolveBlockedExplainRouteSegment,
  type InsuranceBillingType,
} from '../insurance/config';
import { getInsuranceExplainPath, resolveBillingTypeFromPatient } from '../insurance/routing';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

type UseInsuranceExplainBillingGuardOptions = {
  blockedExplainRouteSegment?: string;
  /**
   * Switches the guard off while the screen itself is rewriting `billingType` — read at effect
   * time, which is why it is a ref and not a boolean. A screen that changes the billing type as
   * part of its own exit (the BNPL decline) has to suppress the guard from inside an async handler,
   * between renders; a plain prop would still carry its pre-handler value when the resulting store
   * change re-runs this effect, and the guard would navigate first. See `useBnplPaymentScreen`.
   */
  suppressedRef?: RefObject<boolean>;
};

/**
 * Navigates to the correct explain or select route when billing context changes.
 *
 * @param {string} expectedBillingType Billing type this screen is built for.
 * @param {Object | undefined} options Optional blocked-route segment and suppression ref.
 * @return {void}
 */
export function useInsuranceExplainBillingGuard(
  expectedBillingType: InsuranceBillingType,
  options?: UseInsuranceExplainBillingGuardOptions,
) {
  const navigate = useNavigate();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const blockedExplainRouteSegment = options?.blockedExplainRouteSegment;
  const suppressedRef = options?.suppressedRef;

  useEffect(() => {
    if (!signedUpPatient || suppressedRef?.current) {
      return;
    }
    const billingType = resolveBillingTypeFromPatient(signedUpPatient);
    const insuranceInfo = signedUpPatient.webAppOnboarding?.insuranceInfo;
    const blockedReason = insuranceInfo?.blockedReason;
    const practiceChargesFee = insuranceInfo?.practiceChargesFee === true;
    if (!billingType) {
      navigate('/webAppOnboarding/insurance-select', { replace: true });
      return;
    }
    if (billingType !== expectedBillingType) {
      navigate(getInsuranceExplainPath(billingType, { blockedReason, practiceChargesFee }), { replace: true });
      return;
    }
    if (
      expectedBillingType === 'blocked' &&
      blockedExplainRouteSegment &&
      resolveBlockedExplainRouteSegment({ blockedReason, practiceChargesFee }) !== blockedExplainRouteSegment
    ) {
      navigate(getInsuranceExplainPath('blocked', { blockedReason, practiceChargesFee }), { replace: true });
    }
  }, [signedUpPatient, expectedBillingType, blockedExplainRouteSegment, suppressedRef, navigate]);
}
